import React from "react";
import { resolveMarkerSymbolsInText } from "./StatMarkerUtils";

/* ── Types ──────────────────────────────────────────────────── */

export interface ICIGroup {
	ciLevel: string;
	entries: { code: string; label: string }[];
	rawSegment: string;
}

export interface IParsedStatMarkers {
	prefix: string;
	groups: ICIGroup[];
	isMultiCI: boolean;
	markerRaw: string;
}

export interface ILowNItem {
	code: string;
	label?: string;
}

/* ── Parsing ────────────────────────────────────────────────── */

export const parseMarkerEntries = (raw: string): { code: string; label: string }[] => {
	const entries: { code: string; label: string }[] = [];
	for (const entry of raw.split(",")) {
		const colonIdx = entry.indexOf(":");
		if (colonIdx === -1) continue;
		const code = entry.slice(0, colonIdx).trim();
		const label = entry.slice(colonIdx + 1).trim();
		if (code && label) entries.push({ code, label });
	}
	return entries;
};

export const parseStatMessageMarkers = (statMessage: string): IParsedStatMarkers | null => {
	if (!statMessage) return null;

	const markerIdx = statMessage.indexOf("| Markers-");
	if (markerIdx === -1) return null;

	const prefix = statMessage.slice(0, markerIdx).trim();
	const markerPart = statMessage.slice(markerIdx + "| Markers-".length).trim();

	const groups: ICIGroup[] = [];

	const multiCIRegex = /\{(\d+)\}%\s*\[([^\]]+)\]/g;
	let match: RegExpExecArray | null;
	while ((match = multiCIRegex.exec(markerPart)) !== null) {
		groups.push({
			ciLevel: match[1],
			entries: parseMarkerEntries(match[2]),
			rawSegment: match[2],
		});
	}

	if (groups.length > 1) {
		return { prefix, groups, isMultiCI: true, markerRaw: markerPart };
	}

	groups.length = 0;
	const entries = parseMarkerEntries(markerPart);
	if (entries.length) {
		groups.push({ ciLevel: "", entries, rawSegment: markerPart });
	}

	return groups.length > 0 ? { prefix, groups, isMultiCI: false, markerRaw: markerPart } : null;
};

/* ── Question / src parsing ─────────────────────────────────── */

export const parseSrc = (src?: string): { quesNo?: string; questionText?: string } => {
	if (!src?.trim()) return { quesNo: undefined, questionText: undefined };

	const match = src.trim().match(/^([a-zA-Z0-9_]+)\.\s*([\s\S]*)$/);
	if (match) {
		return { quesNo: match[1]?.trim(), questionText: match[2]?.trim() };
	}
	return { quesNo: undefined, questionText: src.trim() };
};

/* ── Weight label ───────────────────────────────────────────── */

export const getWeightLabel = (isWeighted?: boolean): string | null => {
	if (isWeighted === true) return "data is weighted";
	if (isWeighted === false) return "data is unweighted";
	return null;
};

/* ── Resolve stat message (symbol replacement) ──────────────── */

export const resolveStatMessage = (raw?: string): string | undefined => {
	return raw ? resolveMarkerSymbolsInText(raw) : raw;
};

/* ── Stat marker JSX rendering (shared) ─────────────────────── */

export const renderStatMarkerNode = (parsedStatMarkers: IParsedStatMarkers, hasLowN: boolean): React.ReactNode => {
	if (parsedStatMarkers.isMultiCI) {
		const markerRaw = parsedStatMarkers.markerRaw;
		const segments: React.ReactNode[] = [];
		let lastIndex = 0;
		const bracketRegex = /\[([^\]]+)\]/g;
		let bMatch: RegExpExecArray | null;

		while ((bMatch = bracketRegex.exec(markerRaw)) !== null) {
			if (bMatch.index > lastIndex) {
				segments.push(markerRaw.slice(lastIndex, bMatch.index));
			}

			const innerEntries = parseMarkerEntries(bMatch[1]);
			segments.push(
				React.createElement(
					React.Fragment,
					{ key: `bracket-${bMatch.index}` },
					"[",
					...innerEntries.flatMap((entry, eIdx) => {
						const parts: React.ReactNode[] = [];
						if (eIdx > 0) parts.push(", ");
						parts.push(
							React.createElement(
								"span",
								{
									key: `${bMatch!.index}-${entry.code}`,
									className: hasLowN ? "text-[--nSizeLabel-color]" : "",
								},
								entry.code,
							),
						);
						parts.push(": ");
						parts.push(entry.label);
						return parts;
					}),
					"]",
				),
			);

			lastIndex = bMatch.index + bMatch[0].length;
		}

		if (lastIndex < markerRaw.length) {
			segments.push(markerRaw.slice(lastIndex));
		}

		return React.createElement("span", { key: "stat" }, parsedStatMarkers.prefix, " | Markers- ", ...segments);
	}

	// Single CI
	return React.createElement(
		"span",
		{ key: "stat" },
		parsedStatMarkers.prefix,
		" | Markers- ",
		...parsedStatMarkers.groups[0].entries.flatMap((entry, eIdx) => {
			const parts: React.ReactNode[] = [];
			if (eIdx > 0) parts.push(", ");
			parts.push(
				React.createElement(
					"span",
					{
						key: entry.code,
						className: hasLowN ? "text-[--nSizeLabel-color]" : "",
					},
					entry.code,
				),
			);
			parts.push(": ");
			parts.push(entry.label);
			return parts;
		}),
	);
};

/**
 * Build the full metadata items array (the "second line" of the footnote).
 * Pure function — no hooks. Caller passes all resolved values.
 */
export const buildMetadataItems = (opts: {
	hasLowNSize: boolean;
	analysisName?: string;
	weightLabel: string | null;
	parsedStatMarkers: IParsedStatMarkers | null;
	resolvedStatMessage?: string;
	studyName?: string;
}): React.ReactNode[] => {
	const { hasLowNSize, analysisName, weightLabel, parsedStatMarkers, resolvedStatMessage, studyName } = opts;
	const items: React.ReactNode[] = [];

	if (hasLowNSize) {
		items.push(React.createElement("span", { key: "low-n", className: "text-[--nSizeLabel-color]" }, "Low n-size"));
	}

	if (analysisName) {
		items.push(React.createElement("span", { key: "analysis" }, analysisName));
	}

	if (weightLabel) {
		items.push(React.createElement("span", { key: "weight" }, weightLabel));
	}

	if (parsedStatMarkers) {
		items.push(renderStatMarkerNode(parsedStatMarkers, hasLowNSize));
	} else if (resolvedStatMessage) {
		items.push(React.createElement("span", { key: "stat" }, resolvedStatMessage));
	}

	if (studyName) {
		items.push(React.createElement("span", { key: "study", className: "text-[#4CBB96]" }, studyName));
	}

	return items;
};
